class DuosStickyCheckoutBar extends HTMLElement {
  constructor() {
    super();
    this.cartUpdateUnsubscriber = undefined;
    this._cartSyncTimer = undefined;
    this._drawerObserver = undefined;
    this._shimmerTimer = undefined;
    this._shimmerPlayed = false;
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.classList.add('duos-sticky-checkout--ready');
    });
    this.scheduleOneTimeShimmer();
    this.bindCartDrawerEmptySync();
    this.refreshFromCartJs();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.cartData) {
        this.updateFromCart(event.cartData);
      }
      window.clearTimeout(this._cartSyncTimer);
      this._cartSyncTimer = window.setTimeout(() => this.refreshFromCartJs(), 100);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshFromCartJs();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
    }
    window.clearTimeout(this._cartSyncTimer);
    window.clearTimeout(this._shimmerTimer);
    this.setBodyState(false, false);
  }

  scheduleOneTimeShimmer() {
    if (this._shimmerPlayed) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    window.clearTimeout(this._shimmerTimer);
    this._shimmerTimer = window.setTimeout(() => {
      if (this._shimmerPlayed) return;
      if (this.classList.contains('duos-sticky-checkout--hidden')) return;

      const button = this.querySelector('.duos-sticky-checkout__button');
      if (!button) return;

      this._shimmerPlayed = true;
      button.classList.add('duos-sticky-checkout__button--shimmer-once');
    }, 5000);
  }

  bindCartDrawerEmptySync() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const syncFromDrawer = () => {
      if (drawer.classList.contains('is-empty')) {
        this.setVisibility(false);
      } else {
        this.refreshFromCartJs();
      }
    };

    this._drawerObserver = new MutationObserver(syncFromDrawer);
    this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    syncFromDrawer();
  }

  refreshFromCartJs() {
    fetch(`${window.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => this.updateFromCart(cart))
      .catch(() => {});
  }

  isCartEmpty(cart) {
    if (!cart) return true;
    if (Array.isArray(cart.items)) {
      return cart.items.length === 0;
    }
    const count = Number(cart.item_count);
    return !Number.isFinite(count) || count <= 0;
  }

  updateFromCart(cart) {
    if (this.isCartEmpty(cart)) {
      this.setVisibility(false);
      return;
    }

    const threshold = parseInt(this.dataset.thresholdCents, 10) || 0;
    const subtotal = cart.items_subtotal_price || 0;

    this.setVisibility(true);
    this.updateCartCount(cart);

    const wrap = this.querySelector('[data-free-shipping-wrap]');
    const messageEl = this.querySelector('[data-free-shipping-message]');
    const progressEl = this.querySelector('[data-free-shipping-progress]');
    const track = this.querySelector('.duos-sticky-checkout__shipping-track');
    const strings = window.duosStickyCheckoutStrings || {};

    let showProgress = false;
    if (threshold > 0 && wrap) {
      showProgress = true;
      wrap.hidden = false;
      wrap.classList.remove('duos-sticky-checkout__shipping--disabled');

      const remaining = Math.max(0, threshold - subtotal);
      const percent = Math.min(100, Math.round((subtotal / threshold) * 100));

      if (progressEl) progressEl.style.width = `${percent}%`;
      if (track) track.setAttribute('aria-valuenow', String(percent));

      if (messageEl) {
        if (remaining > 0) {
          const amount = this.formatMoney(remaining, cart.currency);
          messageEl.textContent = (strings.remaining || '').replace('[amount]', amount);
        } else {
          messageEl.textContent = strings.qualified || '';
        }
      }
    } else if (wrap) {
      wrap.hidden = true;
      wrap.classList.add('duos-sticky-checkout__shipping--disabled');
    }

    if (!this.classList.contains('duos-sticky-checkout--hidden')) {
      this.setBodyState(true, showProgress);
    }
  }

  updateCartCount(cart) {
    const countWrap = this.querySelector('[data-sticky-cart-count]');
    const valueEl = this.querySelector('[data-sticky-cart-count-value]');
    if (!countWrap || !valueEl) return;

    let count = 0;
    if (Array.isArray(cart.items)) {
      count = cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    } else {
      const parsed = Number(cart.item_count);
      count = Number.isFinite(parsed) ? parsed : 0;
    }

    if (count > 0 && count < 100) {
      valueEl.textContent = String(count);
      countWrap.hidden = false;
    } else {
      valueEl.textContent = '';
      countWrap.hidden = count <= 0;
    }
  }

  formatMoney(cents, currencyCode) {
    const currency = currencyCode || this.dataset.currency || 'EUR';
    const locale = this.dataset.locale || document.documentElement.lang || 'sk-SK';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'CZK' ? 0 : 2,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  setVisibility(visible) {
    if (visible) {
      this.classList.remove('duos-sticky-checkout--hidden');
      this.setAttribute('aria-hidden', 'false');
      return;
    }

    this.classList.add('duos-sticky-checkout--hidden');
    this.setAttribute('aria-hidden', 'true');
    window.clearTimeout(this._hidePaddingTimer);
    this._hidePaddingTimer = window.setTimeout(() => {
      if (this.classList.contains('duos-sticky-checkout--hidden')) {
        this.setBodyState(false, false);
      }
    }, 360);
  }

  setBodyState(visible, withProgress) {
    document.body.classList.toggle('duos-has-sticky-checkout', visible);
    document.body.classList.toggle('duos-has-sticky-checkout--progress', visible && withProgress);
  }
}

customElements.define('duos-sticky-checkout-bar', DuosStickyCheckoutBar);
